import React from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useModals } from "hooks";
import { IApiServiceError, useDealActions } from "store";
import { ISubCategory } from "store/types";
import pickFiles from "utils/pick-files";

import { ListOption, Values } from "./Form";

interface Props {
  values: Values;
  setValues: React.Dispatch<React.SetStateAction<Values>>;
  subCategories: ISubCategory[];
  optionsErrors: Record<string, string>;
  setOptionsErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export const ListTab: React.FC<Props> = ({ values, setValues, subCategories, optionsErrors, setOptionsErrors }) => {
  const { downloadCharacteristicOptionsTemplateAction, uploadCharacteristicOptionsAction } = useDealActions();
  const { errorModal } = useModals();

  const clearOptionError = (key: string) => {
    setOptionsErrors((prev) => {
      const newErrors = Object.assign({}, prev);
      delete newErrors[key];
      return newErrors;
    });
  };

  const handleUpdateValue = (id: string, newValue: string) => {
    clearOptionError(`${id}-value`);
    setValues((prev) => ({
      ...prev,
      list_options: prev.list_options?.map((opt) => (opt.id === id ? { ...opt, value: newValue } : opt))
    }));
  };

  const handleUpdateSubcategories = (id: string, selectedSubcategories: ISubCategory[]) => {
    clearOptionError(`${id}-subcategories`);
    setValues((prev) => ({
      ...prev,
      list_options: prev.list_options?.map((opt) => {
        return opt.id === id ? { ...opt, subcategories: selectedSubcategories.map((sub) => sub.id) } : opt;
      })
    }));
  };

  const handleRemoveOption = (id: string) => {
    clearOptionError(`${id}-value`);
    clearOptionError(`${id}-subcategories`);
    setValues((prev) => ({
      ...prev,
      list_options: prev.list_options?.filter((opt) => opt.id !== id)
    }));
  };

  const isAssetReport = values?.category_name === "Asset Report";

  const handleAddOption = () => {
    let selectedSubcategories: number[] = [];

    if (isAssetReport) {
      // Use selected subcategories or fall back to first available
      selectedSubcategories = values.subcategory_ids?.length
        ? values.subcategory_ids
        : subCategories[0]?.id
          ? [subCategories[0].id]
          : [];

      if (selectedSubcategories.length === 0) return;
    }

    const newOption: ListOption = {
      id: crypto.randomUUID(),
      value: "",
      subcategories: selectedSubcategories
    };

    setValues((prev) => ({
      ...prev,
      list_options: [...(prev.list_options || []), newOption],
      ...(isAssetReport && !prev.subcategory_ids?.length && { subcategory_ids: [subCategories[0].id] })
    }));
  };

  const uploadList = async () => {
    try {
      const fileList = await pickFiles({ accept: [".xlsx", ".xls"].join(",") });
      if (!fileList || !fileList[0]) return;
      const importedData = await uploadCharacteristicOptionsAction({
        file: fileList[0]
      }).catch((e) => {
        errorModal({ msg: (e as IApiServiceError).errors.error[0] });
        throw e;
      });

      if (importedData.length) {
        let selectedSubcategories: number[] = [];

        if (isAssetReport) {
          selectedSubcategories = values.subcategory_ids;

          if (!selectedSubcategories.length) {
            errorModal({ msg: "Please select at least one subcategory first" });
            return;
          }
        }

        const newListOptions = importedData.map((value) => ({
          id: crypto.randomUUID(),
          value,
          subcategories: selectedSubcategories
        }));
        setValues((prev) => ({
          ...prev,
          list_options: [...(prev.list_options || []), ...newListOptions]
        }));
      }
    } catch (_error) {
      errorModal({ msg: "Failed to upload list of options" });
    }
  };

  return (
    <Stack sx={{ gap: "16px", flexGrow: 1 }} alignItems="stretch">
      <Stack direction="column" gap="16px" width="100%">
        {values.list_options?.map((option, index) => (
          <Stack key={option.id} direction="row" gap="5px">
            <TextField
              variant="outlined"
              size="small"
              label={`Option ${index + 1}`}
              value={option.value}
              onChange={(e) => handleUpdateValue(option.id, e.target.value)}
              fullWidth
              disabled={values.is_locked_to_edit}
              error={!!optionsErrors[`${option.id}-value`]}
              helperText={optionsErrors[`${option.id}-value`]}
            />
            {isAssetReport && (
              <Autocomplete
                getOptionLabel={(option: ISubCategory) => option.name}
                multiple
                limitTags={1}
                size="small"
                disableClearable
                disableCloseOnSelect
                options={subCategories}
                value={subCategories.filter((subCat) => option.subcategories.includes(subCat.id))}
                onChange={(_, selected) => handleUpdateSubcategories(option.id, selected)}
                disabled={values.is_locked_to_edit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label="Sub Categories"
                    error={!!optionsErrors[`${option.id}-subcategories`]}
                    helperText={optionsErrors[`${option.id}-subcategories`]}
                  />
                )}
                sx={{
                  flexGrow: 1,
                  maxWidth: "200px",
                  "& .MuiAutocomplete-inputRoot": {
                    flexWrap: "nowrap !important",
                    overflow: "hidden"
                  },
                  "& .MuiAutocomplete-tag": {
                    maxWidth: "calc(100% - 30px)"
                  }
                }}
              />
            )}
            {!values.is_locked_to_edit && (
              <IconButton
                onClick={() => handleRemoveOption(option.id)}
                sx={{ backgroundColor: "#f5f6f7 !important", mt: "5px" }}
              >
                <CloseIcon sx={{ color: "#071F35" }} />
              </IconButton>
            )}
          </Stack>
        ))}
        {!values.is_locked_to_edit && (
          <IconButton
            onClick={handleAddOption}
            sx={{
              bgcolor: "#f5f6f7",
              alignSelf: "flex-start"
            }}
          >
            <AddIcon sx={{ color: "#071F35" }} />
          </IconButton>
        )}
      </Stack>
      {!values.is_locked_to_edit && (
        <Box
          sx={{
            bgcolor: "grey.100",
            p: "16px",
            borderRadius: "8px",
            mt: "16px"
          }}
        >
          <Typography variant="body2" sx={{ mb: "12px" }}>
            You are able to use a template to create a list. Download the template, fill it and then upload it again.
          </Typography>
          <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              onClick={downloadCharacteristicOptionsTemplateAction}
              sx={{ fontSize: "16px", color: "#26A6AE", textDecoration: "underline", cursor: "pointer" }}
            >
              Download template
            </Typography>
            <Button variant="outlined" onClick={uploadList} sx={{ fontSize: "12px" }}>
              Upload the list
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
